import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import { useLeague } from 'contexts/LeagueContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const HomePage = () => {
  const { players, games, gameStats, gamesLoading, playersLoading, getPlayerStats, getComboStats } = useApp();
  const { activeLeague, leagueMembers, userRole } = useLeague();

  const loading = gamesLoading || playersLoading;

  const topPlayers = useMemo(() => {
    return players
      ?.map((p) => {
        const s = getPlayerStats(p?.id);
        return { id: p?.id, name: p?.name, gamesPlayed: s?.gamesPlayed || 0, winPct: s?.winPct || 0, avgPoints: s?.avgPoints || 0 };
      })
      ?.filter((p) => p.gamesPlayed > 0)
      ?.sort((a, b) => b.winPct - a.winPct || b.gamesPlayed - a.gamesPlayed)
      ?.slice(0, 3);
  }, [players, getPlayerStats]);

  const recentGames = useMemo(() => {
    const playerMap = {};
    players?.forEach((p) => { playerMap[p?.id] = p; });
    const statsByGame = {};
    gameStats?.forEach((s) => {
      if (!statsByGame[s?.game_id]) statsByGame[s.game_id] = [];
      statsByGame[s.game_id].push(s);
    });

    return games?.slice(0, 3)?.map((game) => {
      const stats = statsByGame[game?.id] || [];
      const winnersScore = stats?.filter((s) => s?.team === game?.winning_team)?.reduce((sum, s) => sum + (s?.points || 0), 0);
      const losersScore = stats?.filter((s) => s?.team !== game?.winning_team)?.reduce((sum, s) => sum + (s?.points || 0), 0);
      return { id: game?.id, date: game?.date, location: game?.location, winnersScore, losersScore };
    });
  }, [games, gameStats, players]);

  const positionBreakdown = useMemo(() => {
    const counts = {};
    players?.forEach((p) => {
      const pos = p?.position || '?';
      counts[pos] = (counts[pos] || 0) + 1;
    });
    return counts;
  }, [players]);

  const topDuos = useMemo(() => {
    const combos = getComboStats();
    return combos?.duos?.slice(0, 3) || [];
  }, [getComboStats]);

  // Activity feed: recent games + recent member joins, merged + sorted by date
  const activityFeed = useMemo(() => {
    const playerMap = {};
    players?.forEach((p) => { playerMap[p?.id] = p; });

    const statsByGame = {};
    gameStats?.forEach((s) => {
      if (!statsByGame[s?.game_id]) statsByGame[s.game_id] = [];
      statsByGame[s.game_id].push(s);
    });

    const gameActivities = (games || []).slice(0, 10).map((game) => {
      const stats = statsByGame[game?.id] || [];
      const winners = stats.filter((s) => s.team === game?.winning_team);
      const topScorer = stats.reduce((top, s) => (!top || (s?.points || 0) > (top?.points || 0)) ? s : top, null);
      const topScorerName = topScorer ? (playerMap[topScorer.player_id]?.name || 'Unknown') : null;
      const topScorerPoints = topScorer?.points || 0;
      const winnersScore = winners.reduce((sum, s) => sum + (s?.points || 0), 0);
      const losersScore = stats.filter((s) => s.team !== game?.winning_team).reduce((sum, s) => sum + (s?.points || 0), 0);
      return {
        type: 'game',
        id: `game-${game.id}`,
        date: game.date,
        sortKey: new Date(game.date).getTime(),
        winnersScore,
        losersScore,
        topScorerName,
        topScorerPoints,
      };
    });

    const memberActivities = (leagueMembers || [])
      .filter((m) => m.joinedAt)
      .slice(0, 10)
      .map((m) => ({
        type: 'join',
        id: `member-${m.id}`,
        date: m.joinedAt,
        sortKey: new Date(m.joinedAt).getTime(),
        name: m.name,
      }));

    return [...gameActivities, ...memberActivities]
      .sort((a, b) => b.sortKey - a.sortKey)
      .slice(0, 5);
  }, [games, gameStats, players, leagueMembers]);

  const formatDate = (dateStr) => {
    try {
      const d = dateStr?.includes('/') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr || '';
    }
  };

  const formatRelativeDate = (dateStr) => {
    try {
      // Parse YYYY-MM-DD as local midnight (avoid UTC interpretation that
      // shifts back a day in negative-offset timezones). ISO timestamps with
      // explicit time / Z stay as-is.
      const d = dateStr?.includes('T') || dateStr?.includes('Z')
        ? new Date(dateStr)
        : new Date(dateStr + 'T00:00:00');
      const now = new Date();
      const diffMs = now - d;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>Dashboard</h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
              {activeLeague?.name} — at a glance
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
            </div>
          ) : (
            <>
              {/* League Info Banner */}
              <Link
                to="/members"
                className="block rounded-xl border mb-4 overflow-hidden transition-all hover:shadow-lg"
                style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', textDecoration: 'none', color: 'inherit' }}
              >
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(232,93,4,0.12)' }}>
                      <Icon name="Shield" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h2 className="font-heading font-semibold text-base" style={{ color: 'var(--color-foreground)' }}>
                        {activeLeague?.name}
                      </h2>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                          {leagueMembers?.length} member{leagueMembers?.length !== 1 ? 's' : ''}
                        </span>
                        <span className="font-caption text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}>
                          {ROLE_LABELS[userRole] || 'Member'}
                        </span>
                        <span className="font-data text-xs tracking-wider" style={{ color: 'var(--color-muted-foreground)' }}>
                          Code: {activeLeague?.code}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={18} color="var(--color-muted-foreground)" />
                </div>
              </Link>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Top Players Card */}
                <DashboardCard title="Top Players" icon="Trophy" to="/stats">
                  {topPlayers?.length > 0 ? (
                    <div className="space-y-2">
                      {topPlayers.map((p, i) => (
                        <div key={p.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-data text-xs font-bold w-5 text-center" style={{ color: i === 0 ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                              {i + 1}
                            </span>
                            <span className="font-heading text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{p.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{p.avgPoints.toFixed(1)} PPG</span>
                            <span className="font-data text-sm font-bold" style={{ color: 'var(--color-primary)' }}>{p.winPct.toFixed(0)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState text="No games recorded yet" linkText="Record a Game" linkTo="/game-recording" />
                  )}
                </DashboardCard>

                {/* Top Duos / Chemistry Card */}
                <DashboardCard title="Best Chemistry" icon="Users" to="/stats">
                  {topDuos?.length > 0 ? (
                    <div className="space-y-2">
                      {topDuos.map((d, i) => (
                        <div key={d.playerIds.join('-')} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-data text-xs font-bold w-5 text-center flex-shrink-0" style={{ color: i === 0 ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                              {i + 1}
                            </span>
                            <span className="font-heading text-sm font-medium truncate" style={{ color: 'var(--color-foreground)' }}>
                              {d.playerNames?.join(' & ')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                              {d.wins}W-{d.games - d.wins}L
                            </span>
                            <span className="font-data text-sm font-bold" style={{ color: 'var(--color-primary)' }}>{d.winPct?.toFixed(0)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState text="Play 2+ games together to see top duos" />
                  )}
                </DashboardCard>

                {/* Recent Games Card */}
                <DashboardCard title="Recent Games" icon="Clock" to="/history">
                  {recentGames?.length > 0 ? (
                    <div className="space-y-2">
                      {recentGames.map((g) => (
                        <div key={g.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon name="Calendar" size={12} color="var(--color-muted-foreground)" />
                            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{formatDate(g.date)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-data text-sm font-bold" style={{ color: '#059669' }}>{g.winnersScore}</span>
                            <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>-</span>
                            <span className="font-data text-sm font-bold" style={{ color: '#DC2626' }}>{g.losersScore}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState text="No games recorded yet" linkText="Record a Game" linkTo="/game-recording" />
                  )}
                </DashboardCard>

                {/* Roster Card */}
                <DashboardCard title="Roster" icon="Dribbble" to="/players">
                  {players?.length > 0 ? (
                    <div>
                      <p className="font-data text-2xl font-bold mb-2" style={{ color: 'var(--color-foreground)' }}>
                        {players.length} <span className="font-caption text-sm font-normal" style={{ color: 'var(--color-muted-foreground)' }}>player{players.length !== 1 ? 's' : ''}</span>
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(positionBreakdown).sort((a, b) => b[1] - a[1]).map(([pos, count]) => (
                          <span
                            key={pos}
                            className="font-caption text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}
                          >
                            {count} {pos}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState text="No players added yet" linkText="Add Players" linkTo="/players" />
                  )}
                </DashboardCard>

                {/* Activity Feed Card — full width */}
                <div className="md:col-span-2">
                  <div
                    className="rounded-xl border overflow-hidden"
                    style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <div className="flex items-center gap-2">
                        <Icon name="Activity" size={16} color="var(--color-primary)" />
                        <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>Activity Feed</h3>
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      {activityFeed?.length > 0 ? (
                        <ul className="space-y-2.5">
                          {activityFeed.map((item) => (
                            <li key={item.id} className="flex items-start gap-3">
                              <div
                                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                style={{
                                  backgroundColor: item.type === 'game' ? 'rgba(232,93,4,0.12)' : 'rgba(22,163,74,0.12)',
                                }}
                              >
                                <Icon
                                  name={item.type === 'game' ? 'Trophy' : 'UserPlus'}
                                  size={13}
                                  color={item.type === 'game' ? 'var(--color-primary)' : '#16a34a'}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                {item.type === 'game' ? (
                                  <p className="font-caption text-xs" style={{ color: 'var(--color-foreground)' }}>
                                    Game played — <span className="font-data font-semibold">{item.winnersScore}-{item.losersScore}</span>
                                    {item.topScorerName && (
                                      <span>
                                        {' · '}
                                        <span className="font-semibold">{item.topScorerName}</span> dropped {item.topScorerPoints} pts
                                      </span>
                                    )}
                                  </p>
                                ) : (
                                  <p className="font-caption text-xs" style={{ color: 'var(--color-foreground)' }}>
                                    <span className="font-semibold">{item.name}</span> joined the league
                                  </p>
                                )}
                                <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                                  {formatRelativeDate(item.date)}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <EmptyState text="No activity yet — record a game to get started" />
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

const DashboardCard = ({ title, icon, to, children }) => (
  <Link
    to={to}
    className="block rounded-xl border overflow-hidden transition-all hover:shadow-lg"
    style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', textDecoration: 'none', color: 'inherit' }}
  >
    <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
      <div className="flex items-center gap-2">
        <Icon name={icon} size={16} color="var(--color-primary)" />
        <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{title}</h3>
      </div>
      <Icon name="ChevronRight" size={14} color="var(--color-muted-foreground)" />
    </div>
    <div className="px-4 py-3">
      {children}
    </div>
  </Link>
);

const EmptyState = ({ text, linkText, linkTo }) => (
  <div>
    <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{text}</p>
    {linkText && linkTo && (
      <Link
        to={linkTo}
        className="inline-flex items-center gap-1 mt-2 font-caption text-xs font-semibold"
        style={{ color: 'var(--color-primary)' }}
      >
        {linkText}
        <Icon name="ArrowRight" size={12} color="currentColor" />
      </Link>
    )}
  </div>
);

export default HomePage;
